import { Component, OnInit, ViewChild } from '@angular/core';
import { Observable, tap } from 'rxjs';
import { AuditTrailTransactionNotesReconciliationItem } from './audit-trail-transaction-notes-reconciliation-item.model';
import moment from 'moment';
import {
  Column,
  ExcelExportService,
  FilterService,
  GridComponent,
  GridModule,
  PageService,
  SearchService,
  SelectionService,
  SortService,
  ToolbarService
} from '@syncfusion/ej2-angular-grids';
import { AuditTrailTransactionNotesReconciliationService } from './audit-trail-transaction-notes-reconciliation.service';
import { AsyncPipe, DecimalPipe } from '@angular/common';
import { DateTimePickerModule } from '@syncfusion/ej2-angular-calendars';
import { DialogComponent, DialogModule } from '@syncfusion/ej2-angular-popups';
import { AuditTrailTransactionNotesReconciliationPreviewComponent } from './audit-trail-transaction-notes-reconciliation-preview.component';
import { ClickEventArgs } from '@syncfusion/ej2-angular-navigations';
import { ID_GENERATE_TRANSACTION_NOTE, TOOLBAR_ITEM_GENERATE_TRANSACTION_NOTE } from '../../shared/constants';
import { environment } from '../../../environments/environment';
import { SpinnerComponent } from '../../shared/spinner.component';

@Component({
  selector: 'eaa-audit-trail-transaction-notes-reconciliation',
  providers: [
    ExcelExportService,
    FilterService,
    PageService,
    SearchService,
    SelectionService,
    SortService,
    ToolbarService
  ],
  imports: [
    AsyncPipe,
    AuditTrailTransactionNotesReconciliationPreviewComponent,
    DateTimePickerModule,
    DecimalPipe,
    DialogModule,
    GridModule,
    SpinnerComponent
  ],
  template: `
    <div class="card">
      <div class="card-header d-flex justify-content-between align-items-center">
        <div>
          <h5 class="card-title">TRANSACTION NOTES RECONCILIATION</h5>
          <h6 class="card-subtitle text-body-secondary">Audit Trail</h6>
        </div>
        <div class="d-flex align-items-center gap-3">
          <ejs-datetimepicker
            [showClearButton]="false"
            [enabled]="!searching"
            (change)="dateRangeSelected()"
            [max]="today"
            placeholder="Start"
            floatLabelType="Auto"
            [(value)]="start"
            format="dd/MM/yyyy HH:mm:ss z"
            width="260px"
          />
          <ejs-datetimepicker
            [showClearButton]="false"
            [enabled]="!searching"
            (change)="dateRangeSelected()"
            [max]="today"
            placeholder="End"
            floatLabelType="Auto"
            [(value)]="end"
            format="dd/MM/yyyy HH:mm:ss z"
            width="260px"
          />
        </div>
      </div>
      <div class="card-body p-0">
        <ejs-grid
          #grdAuditTrailTransactionNotesReconciliationItems
          id="grdAuditTrailTransactionNotesReconciliationItems"
          [dataSource]="(items$ | async) ?? []"
          [allowPaging]="true"
          [pageSettings]="{ pageSize: 50, pageSizes: ['10', '20', '50', '100', '250', 'All'] }"
          [allowFiltering]="true"
          [allowSorting]="true"
          [filterSettings]="{
            enableCaseSensitivity: false,
            ignoreAccent: true,
            type: 'Excel',
            columns: [{ field: 'portfolio', matchCase: false, operator: 'doesnotcontain', value: 'overall' }]
          }"
          [allowTextWrap]="true"
          [allowExcelExport]="true"
          [toolbar]="['ExcelExport', toolbarItemGenerateNote, 'Search']"
          (toolbarClick)="toolbarClick($event)"
          [allowSelection]="true"
          [selectionSettings]="{ type: 'Multiple', mode: 'Row', checkboxOnly: true }"
          (rowSelected)="onRowSelected($event)"
          (rowDeselected)="onRowDeselected($event)"
        >
          <e-columns>
            <e-column type="checkbox" width="50" />
            <e-column field="id" headerText="Transaction ID" width="160px" [isPrimaryKey]="true" />
            <e-column field="portfolio" headerText="Account" width="120px" />
            <e-column field="tradeDate" headerText="Trade Date" type="date" format="dd/MM/yyyy" width="160px" />
            <e-column field="category" headerText="Type" width="160px" />
            <e-column field="quantity" headerText="Quantity" format="N2" width="160px">
              <ng-template #template let-data>
                <div class="d-flex justify-content-end">
                  {{ data.quantity | number: '1.2-2' }}
                </div>
              </ng-template>
            </e-column>
            <e-column field="quantityCurrency" headerText="Ccy" width="80px" />
            <e-column field="noteGenerated" headerText="Note generated" width="160px">
              <ng-template #template let-data>
                <div class="d-flex justify-content-center">
                  @if (data.noteGenerated) {
                    <i class="bi bi-check text-success"></i>
                  } @else {
                    <i class="bi bi-x text-danger"></i>
                  }
                </div>
              </ng-template>
            </e-column>
            <e-column headerText="Preview" [allowFiltering]="false">
              <ng-template #template let-data>
                <div class="d-flex gap-1 flex-wrap">
                  @if (data.versions && data.versions.length > 0) {
                    @for (version of data.versions; track version) {
                      <button
                        class="btn btn-sm btn-outline-primary border-0"
                        (click)="previewTransactionConfirmation(data.id, version)"
                        [title]="'Preview version ' + version"
                      >
                        <i class="bi bi-eye"></i> v{{ version }}
                      </button>
                    }
                  }
                </div>
              </ng-template>
            </e-column>
            <e-column />
          </e-columns>
          <ng-template #emptyRecordTemplate>
            <div>
              @if (searching) {
                <eaa-spinner style="secondary" />
              } @else {
                <span>No transaction found in the specified period.</span>
              }
            </div>
          </ng-template>
        </ejs-grid>
      </div>
    </div>

    <!-- Preview Dialog -->
    <ejs-dialog
      #previewDialog
      [header]="
        'Transaction Confirmation Preview - Transaction #' + previewTransactionId + ' (v' + previewVersion + ')'
      "
      [visible]="false"
      [isModal]="true"
      [showCloseIcon]="true"
      [closeOnEscape]="true"
      [width]="1200"
      [height]="900"
      [animationSettings]="{ effect: 'Zoom', duration: 400 }"
      [cssClass]="'preview-dialog'"
      (close)="closePreviewDialog()"
    >
      <ng-template #content>
        @if (showPreview) {
          <eaa-audit-trail-transaction-notes-reconciliation-preview
            [version]="previewVersion"
            [transactionId]="previewTransactionId"
          />
        }
      </ng-template>
    </ejs-dialog>
  `
})
export class AuditTrailTransactionNotesReconciliationComponent implements OnInit {
  public items$: Observable<AuditTrailTransactionNotesReconciliationItem[]> | null = null;
  public today = moment().endOf('day').toDate();
  public searching = false;
  public start: Date = moment().subtract(1, 'days').toDate();
  public end: Date = moment(this.today).toDate();
  public processingMessage = '';
  public toolbarItemGenerateNote = TOOLBAR_ITEM_GENERATE_TRANSACTION_NOTE;

  protected previewVersion: number = 0;
  protected previewTransactionId: number = 0;
  protected showPreview: boolean = false;

  @ViewChild('grdAuditTrailTransactionNotesReconciliationItems')
  public grid?: GridComponent;

  @ViewChild('previewDialog')
  protected previewDialog?: DialogComponent;

  constructor(
    private auditTrailTransactionNotesReconciliationService: AuditTrailTransactionNotesReconciliationService
  ) {}

  public ngOnInit(): void {
    this.setAuditTrailItems();
  }

  public dateRangeSelected(): void {
    this.searching = true;
    this.setAuditTrailItems();
  }

  public previewTransactionConfirmation(transactionId: number, version: number): void {
    this.previewTransactionId = transactionId;
    this.previewVersion = version;
    this.showPreview = true;
    this.previewDialog?.show();
  }

  public closePreviewDialog(): void {
    this.showPreview = false;
    this.previewDialog?.hide();
  }

  public toolbarClick(args: ClickEventArgs): void {
    if (args.item.id === 'grdAuditTrailTransactionNotesReconciliationItems_excelexport') {
      this.grid?.excelExport({
        columns: [
          { field: 'id', headerText: 'Transaction ID' } as Column,
          { field: 'portfolio', headerText: 'Account' } as Column,
          { field: 'tradeDate', headerText: 'Trade Date', format: 'dd/MM/yyyy' } as Column,
          { field: 'category', headerText: 'Type' } as Column,
          { field: 'quantity', headerText: 'Quantity', format: 'N2' } as Column,
          { field: 'quantityCurrency', headerText: 'Ccy' } as Column,
          { field: 'noteGenerated', headerText: 'Note Generated' } as Column
        ],
        exportType: 'AllPages',
        fileName: 'Transaction Notes Reconciliation.xlsx'
      });
    } else if (args.item.id === ID_GENERATE_TRANSACTION_NOTE) {
      this.generateTransactionConfirmations();
    }
  }

  public onRowSelected(args: any): void {
    this.updateToolbarState();
  }

  public onRowDeselected(args: any): void {
    this.updateToolbarState();
  }

  private updateToolbarState(): void {
    const selectedRecords = this.grid?.getSelectedRecords() as AuditTrailTransactionNotesReconciliationItem[];
    const hasValidSelection =
      selectedRecords && selectedRecords.length > 0 && selectedRecords.every((item) => !item.noteGenerated);

    this.grid?.enableToolbarItems([ID_GENERATE_TRANSACTION_NOTE], hasValidSelection);
  }

  public generateTransactionConfirmations(): void {
    const selectedRecords = this.grid?.getSelectedRecords() as AuditTrailTransactionNotesReconciliationItem[];

    if (!selectedRecords || selectedRecords.length === 0) {
      return;
    }

    const transactionIds = selectedRecords.map((item) => item.id).join(',');
    const url = `${environment.faReportsBaseUrl}/tno/transaction-confirmations?transactionIds=${transactionIds}&comment=`;

    window.open(url, '_blank');
    setTimeout(() => {
      this.grid?.clearSelection();
    }, 500);
  }

  private setAuditTrailItems() {
    this.items$ = this.auditTrailTransactionNotesReconciliationService
      .getAuditTrailTransactionNotesReconciliationItems$(this.start, this.end)
      .pipe(tap(() => (this.searching = false)));
  }
}
